import { useState, useCallback, useEffect } from "react";
import type { GuideId, PersistedGuideState } from "../types";

const STORAGE_KEY = "guide-state";
const CURRENT_VERSION = 1;

// All available guides for progress tracking
const ALL_GUIDES: GuideId[] = [
  "workflow-inbasket",
  "entity-management",
  "import-wizard",
  "lifecycle-states",
  "column-management",
  "dashboard-overview",
];

const getDefaultState = (): PersistedGuideState => ({
  version: CURRENT_VERSION,
  completedGuides: [],
  onboardingDismissed: false,
  completedAt: {},
});

/**
 * Load and migrate persisted state from localStorage
 */
const loadPersistedState = (): PersistedGuideState => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as PersistedGuideState;
      // Handle version migrations if needed
      if (parsed.version !== CURRENT_VERSION) {
        // Future: add migration logic here
        return { ...getDefaultState(), ...parsed, version: CURRENT_VERSION };
      }
      return parsed;
    }
  } catch (error) {
    console.error("Failed to parse guide state:", error);
  }
  return getDefaultState();
};

/**
 * Save state to localStorage
 */
const savePersistedState = (state: PersistedGuideState): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (error) {
    console.error("Failed to save guide state:", error);
  }
};

export interface UseGuideStateResult {
  // State
  completedGuides: string[];
  onboardingDismissed: boolean;
  isFirstTimeUser: boolean;

  // Actions
  markGuideComplete: (guideId: GuideId) => void;
  dismissOnboarding: () => void;
  resetProgress: () => void;

  // Queries
  isGuideComplete: (guideId: GuideId) => boolean;
  getProgress: () => { completed: number; total: number };
}

/**
 * Hook for managing guide completion state with localStorage persistence
 * Follows the pattern from useColumnPreferences.ts
 */
export const useGuideState = (): UseGuideStateResult => {
  const [persistedState, setPersistedState] =
    useState<PersistedGuideState>(loadPersistedState);

  // Persist changes to localStorage
  useEffect(() => {
    savePersistedState(persistedState);
  }, [persistedState]);

  // Determine if this is a first-time user
  const isFirstTimeUser =
    persistedState.completedGuides.length === 0 &&
    !persistedState.onboardingDismissed;

  // Mark a guide as completed
  const markGuideComplete = useCallback((guideId: GuideId) => {
    setPersistedState((prev) => {
      if (prev.completedGuides.includes(guideId)) {
        return prev;
      }
      return {
        ...prev,
        completedGuides: [...prev.completedGuides, guideId],
        completedAt: {
          ...prev.completedAt,
          [guideId]: new Date().toISOString(),
        },
      };
    });
  }, []);

  // Dismiss onboarding welcome
  const dismissOnboarding = useCallback(() => {
    setPersistedState((prev) => ({
      ...prev,
      onboardingDismissed: true,
    }));
  }, []);

  // Reset all progress
  const resetProgress = useCallback(() => {
    setPersistedState(getDefaultState());
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      console.error("Failed to remove guide state:", error);
    }
  }, []);

  // Check if a guide is complete
  const isGuideComplete = useCallback(
    (guideId: GuideId): boolean => {
      return persistedState.completedGuides.includes(guideId);
    },
    [persistedState.completedGuides],
  );

  // Get overall progress
  const getProgress = useCallback(() => {
    return {
      completed: persistedState.completedGuides.length,
      total: ALL_GUIDES.length,
    };
  }, [persistedState.completedGuides.length]);

  return {
    completedGuides: persistedState.completedGuides,
    onboardingDismissed: persistedState.onboardingDismissed,
    isFirstTimeUser,
    markGuideComplete,
    dismissOnboarding,
    resetProgress,
    isGuideComplete,
    getProgress,
  };
};
